import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

/*
 * Button — editorial system (design.md).
 * Primary: near-black ink block, rounded-xl (12px), modest weight 500.
 * Secondary/outline: white canvas + 1px hairline outline — the design.md pair.
 * Emphasis comes from the color block, not shadow or bold weight.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
        outline:
          "border border-border bg-background text-foreground hover:bg-secondary dark:hover:bg-secondary",
        secondary:
          "border border-border bg-background text-foreground hover:bg-secondary dark:hover:bg-secondary",
        ghost:
          "text-foreground hover:bg-secondary dark:hover:bg-secondary",
        link: "text-[var(--color-link)] underline-offset-4 hover:underline dark:text-[var(--color-info-border)]",
      },
      size: {
        default: "h-11 px-5 py-2.5 has-[>svg]:px-4",
        xs: "h-7 gap-1 rounded-sm px-2.5 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-9 gap-1.5 rounded-lg px-3.5 has-[>svg]:px-3",
        lg: "h-12 rounded-xl px-7 text-base has-[>svg]:px-5",
        icon: "size-11 rounded-lg",
        "icon-xs": "size-7 rounded-sm [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-9 rounded-lg",
        "icon-lg": "size-12 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  type,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  // Default `type="button"` when rendering as a real <button> to stop
  // accidental form submits when nested in a <form> (e.g. PromptInput).
  // The "New chat" button reloading the page was caused by the
  // browser's HTML default of type="submit" inside a form context.
  // asChild means we're rendering some other element — leave type alone.
  const resolvedType = !asChild && type === undefined ? 'button' : type

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
      {...(resolvedType !== undefined && { type: resolvedType })}
    />
  )
}

export { Button, buttonVariants }
